/**
 * Persisted Build Itinerary progress so a refresh — or navigating away without
 * cancelling — can resume the wizard where you left off. Only meaningful once the
 * import has been confirmed into a (hidden draft) tournament: resume is keyed off
 * that calendarEventId, and the caller validates the draft still exists before
 * restoring. Tenant-scoped by organization id (mirrors scheduleScopeStorage).
 */

import { LAST_STEP } from "./wizardSteps";

const STORAGE_KEY_PREFIX = "buildItineraryProgress";

export interface WizardProgress {
  /** The draft tournament the wizard is building. */
  calendarEventId: string;
  /** The event name, for the resumed banner + event banner. */
  eventName: string;
  /** The step the coach was on (2..LAST_STEP; step 1 has no tournament yet). */
  currentStep: number;
}

const getStorageKey = (organizationId: string): string =>
  `${STORAGE_KEY_PREFIX}_${organizationId}`;

const isProgress = (value: unknown): value is WizardProgress => {
  if (!value || typeof value !== "object") return false;
  const p = value as Record<string, unknown>;
  return (
    typeof p.calendarEventId === "string" &&
    p.calendarEventId.length > 0 &&
    typeof p.eventName === "string" &&
    typeof p.currentStep === "number" &&
    p.currentStep >= 2 &&
    p.currentStep <= LAST_STEP
  );
};

/** Read saved progress for an org, or null when there's none / it's malformed. */
export const loadWizardProgress = (
  organizationId: string | null,
): WizardProgress | null => {
  if (!organizationId) return null;
  try {
    const raw = localStorage.getItem(getStorageKey(organizationId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isProgress(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

/** Persist progress for an org. No-ops without an org or for invalid progress. */
export const saveWizardProgress = (
  organizationId: string | null,
  progress: WizardProgress,
): void => {
  if (!organizationId || !isProgress(progress)) return;
  try {
    localStorage.setItem(getStorageKey(organizationId), JSON.stringify(progress));
  } catch {
    /* ignore quota / serialization errors — resume is best-effort */
  }
};

/** Forget saved progress for an org (on finish, cancel, or start-over). */
export const clearWizardProgress = (organizationId: string | null): void => {
  if (!organizationId) return;
  try {
    localStorage.removeItem(getStorageKey(organizationId));
  } catch {
    /* ignore */
  }
};
